<template>
  <v-card class="d-flex flex-column align-center pa-6">
    <div class="d-flex flex-row justify-center">
      <div>
        <HappyGuy />
      </div>
      <div class="suus__content-container">
        <Subtitle :line-height="20">{{ headerText }}</Subtitle>
        <TextBody class="mt-2" :font-size="0.875">
          <div
            v-if="pushAlreadyActivated"
            v-html="t$('.description.activated')"
          ></div>
        </TextBody>
        <div class="mt-4" v-if="!pushAlreadyActivated">
          <ActivationWizardStep
            class="mt-2"
            :state="getPermittedState"
            :text="t$('.installer.permissions')"
          />
          <ActivationWizardStep
            class="mt-2"
            :state="getRegisterState"
            :text="t$('.installer.registered')"
          />
          <ActivationWizardStep
            class="mt-2"
            :state="getSendState"
            :text="t$('.installer.push_sent')"
          />
          <ActivationWizardStep
            class="mt-2"
            :state="getReceiveState"
            :text="t$('.installer.push_received')"
          />
          <div v-if="showRetries">
            <TextBody :font-size="0.875" class="mt-4">
              Versuch {{ receiveTries }} von 3
            </TextBody>
          </div>
          <div v-if="getMessage">
            <TextBody
              :font-size="0.875"
              :color="getMessage.isError ? 'error' : 'highlight'"
              class="mt-4"
            >
              {{ getMessage.message }}
            </TextBody>
          </div>
        </div>
      </div>
    </div>
    <Button
      @click="testPush"
      v-if="pushAlreadyActivated"
      class="mt-6"
      :block="true"
      >{{ t$(".buttons.test") }}
    </Button>
    <Button
      @click="repairPush"
      v-else-if="!pushAlreadyActivated && currentStepState == 'error'"
      class="mt-6"
      :block="true"
      >{{ t$(".buttons.repair") }}
    </Button>
    <Button
      @click="$emit('close')"
      v-else-if="!pushAlreadyActivated && success"
      class="mt-6"
      :block="true"
      >{{ t$(".buttons.understand") }}
    </Button>
    <TextButton
      :label="t$('.buttons.close')"
      @click="$emit('close')"
      class="mt-2"
      :block="true"
      v-if="!success"
    ></TextButton>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, PropType, Ref, ref } from "vue"
import Button from "components/UI/Button.vue"
import HappyGuy from "./assets/HappyGuy.svg"
import TextBody from "components/UI/TextBody.vue"
import TextButton from "components/UI/TextButton.vue"
import useTranslate from "features/common/translate/concerns/useTranslate"
import ActivationWizardStep from "features/push/ActivationWizardStep.vue"
import { State as StepState } from "features/push/ActivationWizardStep.vue"
import Subtitle from "components/UI/Subtitle.vue"

type ActivationStepFunction = (optionalFlag?: boolean) => Promise<boolean>
type StepId = "permitted" | "registered" | "sent" | "received"
type Step = {
  id: StepId
  fn: ActivationStepFunction
  resetTo?: StepId
}
type CurrentStepState = "running" | "error"

export default defineComponent({
  name: "NotificationActivationWizard",
  components: {
    Subtitle,
    TextButton,
    TextBody,
    Button,
    HappyGuy,
    ActivationWizardStep,
  },
  props: {
    alreadyActivated: {
      type: Boolean,
      default: false,
    },
    requestNotificationPermission: {
      type: Function as PropType<ActivationStepFunction>,
      required: true,
    },
    isNotificationPermissionGranted: {
      type: Boolean,
      required: true,
    },
    registerDevice: {
      type: Function as PropType<ActivationStepFunction>,
      required: true,
    },
    sendTestPush: {
      type: Function as PropType<ActivationStepFunction>,
      required: true,
    },
    waitForPush: {
      type: Function as PropType<ActivationStepFunction>,
      required: true,
    },
  },
  computed: {
    showRetries(): boolean {
      if (this.currentStep == null || this.currentStepState == "error") {
        return false
      }
      return (
        this.currentStep?.id == "received" || this.currentStep?.id == "sent"
      )
    },
    getMessage(): { message: string; isError: boolean } | null {
      if (this.success) {
        return { message: this.t$(".success"), isError: false }
      } else if (this.currentStepState == "error") {
        return { message: this.t$(".error"), isError: true }
      } else {
        return null
      }
    },
    success(): boolean {
      return this.currentStep == null
    },
    headerText(): string {
      const activatedText = this.t$(".header.activated")
      const successText = this.t$(".header.success")
      const activatingText = this.t$(".header.activating")
      return this.pushAlreadyActivated
        ? activatedText
        : this.success
        ? successText
        : activatingText
    },
    getPermittedState(): StepState {
      return this.getStepState(
        this.currentStep,
        this.currentStepState,
        "permitted"
      )
    },
    getRegisterState(): StepState {
      return this.getStepState(
        this.currentStep,
        this.currentStepState,
        "registered"
      )
    },
    getSendState(): StepState {
      return this.getStepState(this.currentStep, this.currentStepState, "sent")
    },
    getReceiveState(): StepState {
      return this.getStepState(
        this.currentStep,
        this.currentStepState,
        "received"
      )
    },
  },
  data() {
    return {
      receiveTries: 1,
      pushAlreadyActivated: this.alreadyActivated,
    }
  },
  async mounted() {
    if (this.pushAlreadyActivated) {
      return
    }
    const startStep = this.steps.find((step) => step.id == "registered")
    this.install(startStep)
  },
  methods: {
    getStepState(
      currentStep: Step,
      currentStepState: CurrentStepState,
      id: StepId
    ): StepState {
      if (currentStep == null) {
        return "success"
      }
      if (currentStep.id == id) {
        return currentStepState == "running" ? "active" : "error"
      }
      const stepIndex = this.steps.findIndex((step) => step.id == id)
      const currentStepIndex = this.steps.findIndex(
        (step) => step.id == currentStep.id
      )
      return stepIndex < currentStepIndex ? "success" : "waiting"
    },
    repairPush() {
      this.currentStepState = null
      this.receiveTries = 1
      this.install(null, true)
    },
    testPush() {
      this.pushAlreadyActivated = false
      const startStep = this.steps.find((step) => step.id == "sent")
      this.install(startStep)
    },
    async install(startStep?: Step, optionalFlag?: boolean) {
      this.currentStep = startStep || this.steps[0]
      while (this.currentStep != null) {
        this.currentStepState = "running"
        const success = await this.currentStep.fn(optionalFlag)
        if (!success) {
          if (this.currentStep.resetTo && this.receiveTries < 3) {
            this.receiveTries++
            this.currentStep = this.steps.find(
              (step) => step.id == this.currentStep.resetTo
            )
            continue
          }
          this.currentStepState = "error"
          return
        }
        this.currentStep =
          this.steps[this.steps.indexOf(this.currentStep) + 1] ?? null
      }
    },
  },
  setup(props) {
    const steps: Step[] = [
      {
        id: "permitted",
        fn: props.requestNotificationPermission,
      },
      {
        id: "registered",
        fn: props.registerDevice,
      },
      { id: "sent", fn: props.sendTestPush },
      {
        id: "received",
        fn: props.waitForPush,
        resetTo: "sent",
      },
    ]

    const currentStep: Ref<Step | null> = ref(steps[0])
    const currentStepState: Ref<CurrentStepState | null> = ref(null)

    const { t$ } = useTranslate("components.push_wizard.activation_wizard")
    return { t$, steps, currentStep, currentStepState }
  },
})
</script>

<style scoped>
.suus__content-container {
  margin-left: 20px;
}

@media screen and (max-width: 360px) {
  .suus__content-container {
    margin-left: 8px;
  }
}
</style>
